import { useState } from 'react';
import type { Ctx } from '../../ctx';
import { ago, collapseRuns, eventsFor, fmt, slackLine } from '../../model';
import { Empty, Sparkline, VerdictChip } from '../../ui';
import type { MonitorEvent, Pipeline, WebhookEntry } from '../../types';

const numOf = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
const arrOf = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export default function MonitorTab({
  ctx,
  entry,
  pipeline,
}: {
  ctx: Ctx;
  entry: WebhookEntry;
  pipeline: Pipeline;
}) {
  const [check, setCheck] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('');
  const { data, env } = ctx;
  const mon = data.service?.monitor;
  const key = pipeline.key;
  const dial = data.dials?.[key];
  const off = !data.dials?.monitor.enabled || !dial?.enabled;

  const latest = ctx.latest.filter((e) => pipeline.checks.includes(e.check));
  const chk = (c: string): MonitorEvent | undefined => latest.find((e) => e.check === c);

  const scoped = eventsFor(key, pipeline.checks, data.monitor?.events ?? []);
  const filtered = scoped.filter(
    (e) => (!check || e.check === check) && (!verdictFilter || e.verdict === verdictFilter),
  );
  const runs = collapseRuns(filtered);
  const alerts = scoped.filter((e) => e.alerted);

  const rows = key === 'tts' ? data.tts : data.calendly;
  const last = rows.length ? ('first_seen' in rows[0] ? rows[0].first_seen : rows[0].created_at) : null;
  const expected = numOf(chk('tts_silence')?.detail?.expected) ?? 0;
  const ttsAmber = mon?.ttsAmber ?? 0;
  const ttsRed = mon?.ttsRed ?? 1;

  const cardBody = (c: string) => {
    const e = chk(c);
    const d = e?.detail ?? {};
    switch (c) {
      case 'tts_silence':
        return (
          <>
            <p className="note" style={{ margin: '6px 0 10px' }}>
              Submissions that <em>should</em> have arrived since the last one, with none arriving.
              Rates/h: peak {mon?.ttsRates.peak ?? '—'} · offpeak {mon?.ttsRates.offpeak ?? '—'} ·
              weekend {mon?.ttsRates.weekend ?? '—'} (peak {mon ? `${mon.peakStartUtc}–${mon.peakEndUtc}Z Mon–Fri` : '—'}).
            </p>
            <Sparkline exp={expected} amber={ttsAmber} red={ttsRed} />
            <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
              <span className="tab">
                <strong>{expected}</strong> expected · 0 arrived
              </span>
              <span className="dim tab">
                amber ≥ {ttsAmber} · red ≥ {ttsRed}
              </span>
            </div>
            <div className="bar" style={{ marginTop: 6 }}>
              <i
                style={{
                  width: `${Math.min(100, (expected / ttsRed) * 100)}%`,
                  background:
                    expected >= ttsRed
                      ? 'var(--bad)'
                      : expected >= ttsAmber
                        ? 'var(--warn)'
                        : 'var(--ok)',
                }}
              />
              <b style={{ left: `${(ttsAmber / ttsRed) * 100}%` }} />
            </div>
            <div className="note" style={{ marginTop: 8 }}>
              last submission {last ? `${fmt(last)} · ${ago(last)}` : '—'} · band{' '}
              <code>{typeof d.band === 'string' ? d.band : '—'}</code>
            </div>
          </>
        );
      case 'webhook_401s': {
        const total = numOf(d.total) ?? 0;
        return (
          <>
            <div className="note">
              Signature rejections on route <code>{pipeline.route401}</code> during the tick (this
              pod). A streak with the secret set ⇒ the stored secret ≠ the webhook's key.
            </div>
            <div style={{ marginTop: 6 }}>
              <strong className="tab">{total}</strong> this tick · amber ≥ {mon?.rejectsAmber ?? '—'}{' '}
              · red ≥ {mon?.rejectsRed ?? '—'}
            </div>
          </>
        );
      }
      case 'tts_stuck': {
        const stale = arrOf(d.stale).length;
        const failed = arrOf(d.failed).length;
        return (
          <>
            <div className="note">
              Pending rows past their lease, or rows that ended failed in the last hour.
            </div>
            <div style={{ marginTop: 6 }}>
              <strong className="tab">{stale}</strong> stale · <strong className="tab">{failed}</strong>{' '}
              failed
            </div>
          </>
        );
      }
      case 'step_errors': {
        const per =
          d.perStep && typeof d.perStep === 'object' ? (d.perStep as Record<string, number>) : {};
        const list = Object.entries(per);
        return (
          <>
            <div className="note">
              <code>steps.*.error</code> on this webhook's rows in the last hour, per step.
            </div>
            <div style={{ marginTop: 6 }}>
              {list.length
                ? list.map(([s, n]) => (
                    <code key={s} style={{ marginRight: 6 }}>
                      {s}: {n}
                    </code>
                  ))
                : 'none'}
            </div>
          </>
        );
      }
      case 'dial_drift': {
        const diffs = arrOf(d.diffs).map(String).filter((x) => x.startsWith(`${key}.`));
        return (
          <>
            <div className="note">
              <code>{key}</code> dial vs <code>RW_MON_INTENT</code>. Posts at amber — a pipeline
              switched off by hand errors nowhere else.
            </div>
            <div style={{ marginTop: 6 }}>{diffs.length ? diffs.join(', ') : 'match'}</div>
          </>
        );
      }
      case 'tts_lost': {
        if (d.skipped)
          return <div className="note">off on this env ({String(d.skipped)}) — <code>RW_MON_TTS_LOST</code></div>;
        const open = arrOf(d.open);
        const lag = numOf(d.cursorLagMin);
        return (
          <>
            <div className="note">
              Webflow submissions with no ledger row, checked from a persisted cursor. Recover or
              acknowledge them in the Pipeline tab.
            </div>
            <div style={{ marginTop: 6 }}>
              <strong className="tab">{open.length}</strong> open · cursor{' '}
              <strong className="tab">{lag ?? '—'}</strong> min behind
              {d.stalled ? ' · stalled' : ''}
              {d.fetchError ? <span className="dim"> · last error: {String(d.fetchError)}</span> : null}
            </div>
          </>
        );
      }
      default:
        return <div className="note">no renderer for this check</div>;
    }
  };

  return (
    <>
      {off ? (
        <div className="notice" style={{ marginBottom: 14 }}>
          {!data.dials?.monitor.enabled ? (
            <>
              Monitor is <strong>off</strong> on {env} (Overview → Service).
            </>
          ) : (
            <>
              This pipeline is <strong>disabled</strong>; its checks are skipped (
              <code>skipped:dial-off</code>).
            </>
          )}{' '}
          Showing the last recorded ticks.
        </div>
      ) : null}

      <section className={`grid ${pipeline.checks.includes('tts_silence') ? 'g2' : 'g3'}`}>
        {pipeline.checks.map((c) => {
          const e = chk(c);
          const v = off ? 'off' : (e?.verdict ?? 'green');
          return (
            <div className="card pad" key={c}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>{c}</strong>
                <VerdictChip v={v} />
              </div>
              {cardBody(c)}
            </div>
          );
        })}
      </section>

      <section className="grid g2">
        <div className="card">
          <div className="row" style={{ padding: '12px 16px', justifyContent: 'space-between' }}>
            <strong>Events · {pipeline.name}</strong>
            <span className="row" style={{ gap: 6 }}>
              <select
                aria-label="filter by check"
                value={check}
                onChange={(e) => setCheck(e.target.value)}
              >
                <option value="">all checks</option>
                {pipeline.checks.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <select
                aria-label="filter by verdict"
                value={verdictFilter}
                onChange={(e) => setVerdictFilter(e.target.value)}
              >
                <option value="">any verdict</option>
                {['green', 'amber', 'red'].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </span>
          </div>
          <div className="timeline" style={{ padding: '0 16px 10px' }}>
            {runs.length ? (
              runs.map((r, i) => (
                <div className="t" key={`${r.check}-${r.at}-${i}`}>
                  <span className="mono dim">
                    {r.n > 1 ? `${fmt(r.from)} → ${fmt(r.to).slice(11)}` : fmt(r.at)}
                  </span>
                  <span className="mono">{r.check}</span>
                  <VerdictChip v={r.verdict} />
                  <span className="dim wrap">
                    {r.n > 1 ? `${r.n} ticks, ${r.action}` : r.action}
                    {numOf(r.detail?.expected) != null ? ` · expected ${String(r.detail.expected)}` : ''}
                    {arrOf(r.detail?.diffs).length ? ` · ${arrOf(r.detail.diffs).map(String).join(', ')}` : ''}
                  </span>
                  <span>{r.alerted ? <span className="chip info">Slack</span> : null}</span>
                </div>
              ))
            ) : (
              <Empty>No events match.</Empty>
            )}
          </div>
        </div>

        <div className="grid" style={{ gap: 14, alignContent: 'start' }}>
          <div className="card pad">
            <h2>Alerts · {pipeline.name}</h2>
            {alerts.length ? (
              alerts.map((a, i) => (
                <div
                  key={`${a.check}-${a.at}-${i}`}
                  style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}
                >
                  <div className="mono dim" style={{ fontSize: 11 }}>
                    {fmt(a.at)}
                  </div>
                  <div style={{ fontSize: 13 }}>{slackLine(a, env, mon)}</div>
                </div>
              ))
            ) : (
              <Empty>Nothing posted for this webhook — that is the goal.</Empty>
            )}
            <p className="note" style={{ margin: '8px 0 0' }}>
              Slack hears red transitions, <code>(still)</code> every{' '}
              {mon ? (mon.repostMin / 60).toFixed(0) : '—'} h while red, and the recovery. Amber is
              logged only.
            </p>
          </div>

          <div className="card pad">
            <h2>Thresholds</h2>
            <dl className="kv">
              {pipeline.checks.includes('tts_silence') ? (
                <>
                  <dt>tts_silence</dt>
                  <dd>
                    amber ≥ {ttsAmber}, red ≥ {ttsRed} expected
                  </dd>
                  <dt>rates/h</dt>
                  <dd className="mono">{mon ? JSON.stringify(mon.ttsRates) : '—'}</dd>
                </>
              ) : null}
              <dt>webhook_401s</dt>
              <dd>
                amber ≥ {mon?.rejectsAmber ?? '—'}, red ≥ {mon?.rejectsRed ?? '—'} / tick
              </dd>
              <dt>step_errors</dt>
              <dd>amber any, red same step ≥ 3</dd>
              {pipeline.checks.includes('tts_lost') ? (
                <>
                  <dt>tts_lost</dt>
                  <dd>
                    {mon?.ttsLost?.enabled === false ? 'off · ' : ''}red on any open miss (grace{' '}
                    {mon?.ttsLost?.graceMin ?? 15} min) or cursor &gt;{' '}
                    {(mon?.ttsLost?.graceMin ?? 15) + (mon?.ttsLost?.stallMin ?? 120)} min behind
                  </dd>
                </>
              ) : null}
              <dt>repost</dt>
              <dd>every {mon?.repostMin ?? '—'} min while red</dd>
            </dl>
            {entry.pipeline === 'tts' ? (
              <p className="note" style={{ margin: '8px 0 0' }}>
                Rates are re-derived from prod submissions: <code>node scripts/tts-rates.js</code>.
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
